import { el, spacer, svgIcon, pressable, closeButton } from "../lib/dom.js";
import { platform } from "../lib/platform/index.js";
import { renderStrategyView } from "../lib/strategy-view.js";
import { storageGet, storageSet, onStorageChange } from "../lib/storage.js";
import { WatchlistStore } from "../lib/watchlist-store.js";
import { journalStore } from "../lib/journal-store.js";
import { presetJournalReviewFilter } from "./journal.js";
import { stockDirectory, type AssetClass } from "../lib/stock-directory.js";
import { classifySymbol } from "../lib/data-providers/registry.js";
import { availableClasses, onDataModeChange } from "../lib/data-mode.js";
import type { OpenChartTab } from "../lib/chart-windows.js";
import { confluenceStore } from "../lib/confluence-store.js";
import { loadBook, saveBook, bookStorageKey, addStrategy, duplicateStrategy, removeStrategy, setActiveStrategy, type StudioBook } from "../lib/studio-store.js";
import { confluenceData, RELATIONAL_TF, DEFAULT_REL, type RelSettings } from "../lib/confluence-data.js";
import {
  lenses,
  verdict,
  READING_LABEL,
  TRADE_TYPES,
  cardFromTradeType,
  setupInfoFor,
  type Card,
  type Cell,
  type Column,
  type ReadingKey,
  type Verdict,
} from "../lib/confluence.js";

const icons = {
  bell: "M8 2a4 4 0 0 0-4 4v3L2.5 11.5h11L12 9V6a4 4 0 0 0-4-4zM6.5 13.5a1.5 1.5 0 0 0 3 0",
  bellFill: "M8 2a4 4 0 0 0-4 4v3L2.5 11.5h11L12 9V6a4 4 0 0 0-4-4z",
  close: "M4 4l8 8M12 4l-8 8",
  plus: "M8 3v10M3 8h10",
  refresh: "M13 8a5 5 0 1 1-1.5-3.5M13 2v3h-3",
  radar: "M8 8m-0.5 0a0.5 0.5 0 1 0 1 0a0.5 0.5 0 1 0-1 0M8 4.5A3.5 3.5 0 0 1 11.5 8M8 1.5A6.5 6.5 0 0 1 14.5 8M8 8l4.5-4.5",
  // scanner = a framed scan window (corner brackets + a sweep line)
  scanner: "M2 5V3h2M12 3h2v2M14 11v2h-2M4 13H2v-2M2 8h12",
  paperplane: "M14 2L7 9M14 2L9.5 14l-2.5-5L2 6.5z",
  // bars = readings × timeframes (replaces the old clock; this picks WHAT is measured, not just when)
  bars: "M3.5 13V8.5M8 13V3.5M12.5 13V6.5",
  // sliders = signal & thresholds
  sliders: "M2 5.5h5M9.5 5.5h4.5M2 10.5h3M7.5 10.5h6.5M9 4v3M5 9v3",
  chevron: "M4 6.5l4 4 4-4",
  lockClosed: "M4.5 7.5V5a3.5 3.5 0 1 1 7 0v2.5M3.5 7.5h9v6h-9z",
  lockOpen: "M4.5 7.5V5a3.5 3.5 0 0 1 6.9-1M3.5 7.5h9v6h-9z",
  copy: "M5.5 5.5h7v7h-7zM3.5 10.5V3.5h7v2",
  // pin (keep a card expanded) — a map-pin teardrop with a hole
  pin: "M8 2.2c-1.7 0-3 1.3-3 3 0 2.1 3 5.6 3 5.6s3-3.5 3-5.6c0-1.7-1.3-3-3-3zM8 4.3a1 1 0 1 0 0 2 1 1 0 0 0 0-2z",
  // flask = the "lab", same glyph as the chart's Strategy Builder tool
  flask: "M6 2h4M7 2v4l-3.5 6a2 2 0 0 0 1.7 3h5.6a2 2 0 0 0 1.7-3L9 6V2M5 10h6",
  search: "M7 2.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM10.4 10.4L14 14",
  // Stacked layers (a plate over two more layers) — opens the Watchlists list.
  boards: "M8 2.5l5.5 3-5.5 3-5.5-3z M2.5 8l5.5 3 5.5-3 M2.5 10.5l5.5 3 5.5-3",
  // Back arrow — inside an open watchlist, returns to the Watchlists list.
  back: "M7 3.5L2.5 8 7 12.5 M2.5 8H13",
};

/** Chart-style circular toolbar button (matches the chart page's iconPill). */
function pillButton(pathD: string, label: string, onClick: () => void, opts: { active?: boolean; color?: string; dim?: boolean; dot?: boolean } = {}): HTMLElement {
  const b = el("button", {
    className: "pill",
    attrs: { "aria-label": label, title: label },
    // Active icon darkens to a stronger grey with a subtle grey circle, rather than filling black.
    style: { width: "30px", height: "30px", padding: "0", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", color: opts.color ?? (opts.active ? "var(--icon-active)" : "var(--text-secondary)"), background: opts.active ? "var(--bg-fill-strong)" : "", position: "relative", flex: "none", opacity: opts.dim ? "0.4" : "1", cursor: opts.dim ? "default" : "pointer" },
    onClick,
  });
  b.appendChild(svgIcon(pathD, 15));
  if (opts.dot) b.appendChild(el("div", { style: { position: "absolute", top: "5px", right: "5px", width: "6px", height: "6px", borderRadius: "50%", background: "var(--accent-blue)" } }));
  if (!opts.dim) pressable(b);
  return b;
}

// Exact prototype palette for the reading sliders (kept literal so they match
// regardless of the app theme tokens).
// Three neutral states — NOT buy/sell. The user sets the low and high bounds and
// decides what each means; the colour only reports which zone the reading is in:
// low zone (blue), no zone (grey), high zone (purple). (D: colour = condition met.)
const PROTO = { track: "#fbfbfa", loBg: "#e6f1fb", hiBg: "#efe9fb", lo: "#4c8dff", hi: "#9b59d0", mid: "#a0a0a6" };

function iconButton(pathD: string, opts: { color?: string; label: string; fill?: boolean; size?: number; onClick: () => void }): HTMLButtonElement {
  const btn = el("button", {
    className: "wl-iconbtn",
    attrs: { "aria-label": opts.label, title: opts.label },
    // Fixed 18×18 hit area so every icon's hover highlight is the same large size
    // as the close ✕ (the icon glyph is centred inside it).
    style: { color: opts.color ?? "rgba(154,154,158,0.85)", display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", padding: "0", borderRadius: "6px" },
    onClick: opts.onClick,
  });
  btn.appendChild(svgIcon(pathD, opts.size ?? 13, { fill: opts.fill }));
  return btn;
}

/** The thin zoned track: green oversold zone (left), red overbought (right), a
 * dot at the reading. Null reading (still loading) shows an empty track. */
function readingTrack(v: number | null, os: number, ob: number): HTMLElement {
  const osPct = Math.round(os * 100);
  const obPct = Math.round(ob * 100);
  const track = el("div", {
    style: {
      position: "relative",
      flex: "1",
      height: "10px",
      borderRadius: "999px",
      // Hairline outline so the track stays legible on a tinted card (the box
      // highlight shares these zone colours, so without it the track blends in).
      border: "1px solid var(--border)",
      boxSizing: "border-box",
      background: PROTO.track,
      backgroundImage: `linear-gradient(90deg, ${PROTO.loBg} 0 ${osPct}%, transparent ${osPct}% ${obPct}%, ${PROTO.hiBg} ${obPct}% 100%)`,
    },
  });
  if (v != null) {
    const color = v <= os ? PROTO.lo : v >= ob ? PROTO.hi : PROTO.mid;
    track.appendChild(
      el("div", {
        style: {
          position: "absolute",
          top: "50%",
          left: `${Math.round(v * 100)}%`,
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          transform: "translate(-50%,-50%)",
          background: color,
          border: "1.5px solid #fff",
          boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
        },
      })
    );
  }
  return track;
}

/** A reading-row label ("%B 30m") as two ADJACENT columns: the reading in a fixed column
 * (sized to the widest, "Stoch"), the timeframe CENTRED in its own fixed column that sits
 * between the reading and the low value — so the timeframe reads as a centred column keyed
 * to the widest reading and the widest low value, and every row lines up. Labels with no
 * timeframe (a lone tf / measure / partner) fill the reading column, timeframe blank. */
const READ_W = 34; // reading column (fits "Stoch")
const TF_W = 42;   // timeframe column (centred)
const LABEL_W = `${READ_W + 6 + TF_W}px`; // reading + gap + timeframe, for the header spacer
function labelCols(label: string): HTMLElement[] {
  const i = label.indexOf(" ");
  return [
    el("div", { text: i < 0 ? label : label.slice(0, i), style: { width: `${READ_W}px`, flex: "none", whiteSpace: "nowrap", overflow: "hidden", font: "var(--font-caption)", color: "var(--text-secondary)" } }),
    el("div", { text: i < 0 ? "" : label.slice(i + 1), style: { width: `${TF_W}px`, flex: "none", textAlign: "center", whiteSpace: "nowrap", font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
  ];
}

/** Header row above the list: the "Bottom / Top" scale labels, only when the
 * board has cards (shortened from the app's "Bottom BB / Top BB"). */
function boardHeader(): HTMLElement {
  // Lives inside the scrolling list (sticky) so it shares the cards' content width
  // — otherwise the list's reserved scrollbar gutter would offset it from the numbers.
  return el("div", { style: { display: "flex", gap: "6px", alignItems: "center", padding: "6px 12px 4px", font: "var(--font-label)", color: "var(--text-secondary)", position: "sticky", top: "0", background: "var(--bg)", zIndex: "2" } }, [
    el("div", { style: { width: LABEL_W, flex: "none" } }),
    el("div", { text: "Low", style: { width: "38px", flex: "none", textAlign: "right", whiteSpace: "nowrap" } }),
    spacer(),
    el("div", { text: "High", style: { width: "38px", flex: "none", textAlign: "left", whiteSpace: "nowrap" } }),
  ]);
}

// The five instrument-agnostic categories a card's bars can show. On hover the
// ticker gives way to these buttons; clicking one expands the card to that
// category, clicking the active one again collapses (see onCategory).
type Cat = "indicator" | "statistical" | "structural" | "cross" | "inter";
const CATS: Array<{ id: Cat; label: string; short: string; icon: string }> = [
  { id: "indicator", label: "Indicator confluence", short: "Conf", icon: "M2 8l3-3 3 4 3-5 2 3" },
  { id: "statistical", label: "Statistical extremes", short: "Stat", icon: "M2 11c2.5 0 3-7 6-7s3.5 7 6 7" },
  { id: "structural", label: "Structural / price-action", short: "Struct", icon: "M2 11h3V7h3V4h4" },
  // v1 (MVP, D-129): the relational lenses (Cross-sectional / Intermarket) are
  // dropped from the MVP — they read across the whole field, need a live chart
  // surface for their settings, and aren't part of the single-instrument
  // build → test → deploy flow. Kept in v2/v3.
];
// The self-referential categories other than "indicator" show one representative
// reading across the card's timeframes (computed from the same cached candles).
// "indicator" keeps the card's own configured readings; cross/inter are relational.
const CAT_READING: Partial<Record<Cat, ReadingKey>> = { statistical: "z", structural: "range" };
type Bar = { label: string; cell: Cell };

interface CardHandlers {
  onChart: () => void;
  onOpenSettings: () => void;
  onCategory: (cat: Cat) => void;
  onAlert: () => void;
  onRemove: () => void;
  onToggleCollapse: () => void;
  onToggleLock: () => void;
  onReorder: (draggedId: string, targetId: string, after: boolean) => void;
  onAddStrategy: () => void;
  onSelectStrategy: (id: string | null) => void;
  onDuplicateStrategy: (id: string) => void;
  onRemoveStrategy: (id: string) => void;
  onRenameStrategy: (id: string, name: string) => void;
  onToggleStrategies: () => void;
}

/** A trade-type pill: the setup's label plus its live verdict, so the pills row
 * can light it. `active` = its board is the one currently shown for the security. */
interface PillSpec { id: string; kind: "card" | "type" | "rel"; label: string; v: Verdict; dir?: "buy" | "sell"; active: boolean; }

/** Relational trade types (D-114 increment 3) read ACROSS the field, not one symbol,
 * so they aren't cards — they map to a relational lens the category view already draws.
 * A pill lights from the security's live cross-sectional rank (leader = top of the
 * pack → high/purple, laggard = bottom → low/blue). Divergence (intermarket) needs a
 * live spread-z evaluator and lands in the next slice. */
// v1 (MVP, D-129): no relational trade-type pills. Leader / laggard (cross-
// sectional) and Divergence (intermarket) are removed from the MVP; they return
// in v2/v3. Empty here so no relational pills render on any card.
const RELATIONAL_TYPES: Array<{ id: string; pill: string; cat: Cat }> = [];
/** |z| of the current pair spread at/above which Divergence fires. */
const DIVERGENCE_Z = 2;
/** k-of-n at an extreme over relational cells (percentile ranks): enough near the top
 * → "sell" slot (high/purple = leader), enough near the bottom → "buy" (low/blue =
 * laggard). Same shape as the confluence verdict, just on cross-sectional values. */
function relationalVerdict(bars: Array<{ cell: Cell }>, os = 0.2, ob = 0.8, k = 2): Verdict {
  const vals = bars.map((b) => b.cell.v).filter((v): v is number => v != null);
  if (vals.length < k) return null;
  if (vals.filter((v) => v >= ob).length >= k) return "sell";
  if (vals.filter((v) => v <= os).length >= k) return "buy";
  return null;
}

/** The default face of a security card (D-114 increment 2): one pill per trade-type
 * setup on the security, lit on the app's calm blue (oversold / buy side) or purple
 * (overbought / sell side) when it fires — never red/green (D-075). A directional
 * setup only lights on its own side; a symmetric one lights on whichever fired. The
 * active pill (whose board is showing) carries the accent ring. Clicking selects it. */
function pillsRow(pills: PillSpec[], onPick: (id: string, kind: "card" | "type" | "rel") => void): HTMLElement {
  const row = el("div", { style: { display: "flex", flexWrap: "wrap", gap: "5px", margin: "3px 0 1px" } });
  for (const p of pills) {
    const fires: Verdict = p.v && (!p.dir || p.v === p.dir) ? p.v : null;
    const bg = fires === "buy" ? PROTO.loBg : fires === "sell" ? PROTO.hiBg : "transparent";
    const fg = fires === "buy" ? PROTO.lo : fires === "sell" ? PROTO.hi : "var(--text-secondary)";
    const border = p.active ? "var(--accent-blue)" : fires ? fg : "var(--border)";
    const btn = el("button", {
      attrs: { title: fires ? `${p.label} — firing (${fires === "buy" ? "low" : "high"})` : `${p.label} — not firing`, "aria-label": p.label },
      style: {
        display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 9px", borderRadius: "999px",
        border: `1px solid ${border}`, background: bg, color: fg, font: "var(--font-caption)", cursor: "pointer",
        boxShadow: p.active ? "inset 0 0 0 1px var(--accent-blue)" : "none", whiteSpace: "nowrap",
      },
      onClick: () => onPick(p.id, p.kind),
    }, [
      el("span", { style: { width: "6px", height: "6px", borderRadius: "50%", flex: "none", background: fires ? fg : "transparent", border: `1px solid ${fires ? "transparent" : "var(--border)"}` } }),
      el("span", { text: p.label }),
    ]);
    row.appendChild(btn);
  }
  return row;
}

/** One card: header (select + symbol + verdict + price + icons) and the reading rows. */
function cardBlock(
  card: Card,
  cells: Cell[],
  price: number | undefined,
  hasAlert: boolean,
  discordActive: boolean,
  hasSibling: boolean,
  chartOpen: boolean,
  collapsed: boolean,
  locked: boolean,
  activeCat: Cat | null,
  bars: Bar[],
  handlers: CardHandlers,
  pills: PillSpec[],
  onPickPill: (id: string, kind: "card" | "type" | "rel") => void,
  book: StudioBook,
  strategiesOpen: boolean
): HTMLElement {
  // The box tint follows what's OPEN, not the underlying card: the active pill's fired
  // verdict (blue = low, purple = high; D-075 — never red/green). At rest (pills only,
  // nothing open) the box is neutral, so each pill's own dot carries its state.
  const openPill = pills.find((p) => p.active);
  const shownV: Verdict = openPill && openPill.v && (!openPill.dir || openPill.v === openPill.dir) ? openPill.v : null;
  const metBg = shownV === "buy" ? PROTO.loBg : shownV === "sell" ? PROTO.hiBg : "var(--bg-elevated)";
  // Hover tooltip = the full company name (D-157), from the stock directory; falls
  // back to the click hint until the directory has loaded or for an unknown symbol.
  const fullName = stockDirectory.name(card.sym);
  const block = el("div", { className: "wl-card", attrs: { title: fullName ? `${fullName} — open chart` : `Open ${card.sym} chart` }, style: { display: "flex", flexDirection: "column", gap: "4px", background: metBg, border: `1px solid ${chartOpen ? "#bcd4ff" : "var(--border)"}`, borderRadius: "12px", padding: "10px 12px", marginBottom: "8px", boxShadow: chartOpen ? "inset 0 0 0 1px #bcd4ff" : "none", cursor: "pointer", transition: "transform .06s ease, box-shadow .12s ease" } });
  // Hover lift — signals the whole card is clickable (opens the chart). A soft drop
  // shadow, kept separate from the blue inset that marks the chart-open card.
  const baseShadow = chartOpen ? "inset 0 0 0 1px #bcd4ff" : "none";
  const hoverShadow = (chartOpen ? "inset 0 0 0 1px #bcd4ff, " : "") + "0 2px 10px rgba(0,0,0,0.12)";
  block.addEventListener("pointerenter", () => { if (!dragId) block.style.boxShadow = hoverShadow; });
  block.addEventListener("pointerleave", () => { if (!dragId) block.style.boxShadow = baseShadow; });
  // The whole card is a button that opens the chart — except the row's own controls
  // (buttons/inputs/selects), which keep their behaviour.
  const isControl = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest("button, input, select");
  // Click opens the security in the single chart tab (one instrument at a time — D-083).
  block.addEventListener("pointerdown", (e) => {
    if (isControl(e.target)) return;
    block.style.transform = "scale(0.99)";
  });
  const resetPress = () => { block.style.transform = ""; };
  block.addEventListener("pointerup", resetPress);
  block.addEventListener("pointerleave", resetPress);
  block.addEventListener("pointercancel", resetPress);
  block.addEventListener("click", (e) => { if (!isControl(e.target)) handlers.onChart(); });

  // Drag to reorder.
  block.draggable = true;
  block.addEventListener("dragstart", (e) => {
    dragId = card.id;
    if (e.dataTransfer) { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", card.id); }
    block.style.opacity = "0.5";
  });
  block.addEventListener("dragend", () => { dragId = null; block.style.opacity = ""; block.style.boxShadow = ""; });
  block.addEventListener("dragover", (e) => {
    e.preventDefault();
    if (!dragId || dragId === card.id) return;
    const r = block.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    block.style.boxShadow = after ? "inset 0 -2px 0 var(--accent-blue)" : "inset 0 2px 0 var(--accent-blue)";
  });
  block.addEventListener("dragleave", () => { block.style.boxShadow = ""; });
  block.addEventListener("drop", (e) => {
    e.preventDefault();
    block.style.boxShadow = "";
    if (dragId && dragId !== card.id) { const r = block.getBoundingClientRect(); handlers.onReorder(dragId, card.id, e.clientY > r.top + r.height / 2); }
  });

  // Fixed header height so swapping the icon row for the taller "Copy me" button (or
  // toggling the buy/sell ticker padding) never changes the card's size.
  const header = el("div", { style: { display: "flex", alignItems: "center", gap: "6px", position: "relative", minHeight: "22px" } });

  // The ticker (plain — the box tint carries the condition, and the back control names
  // the open setup, so there's no lens caption here). D-114.
  const nameBtn = el("div", {
    style: { font: "var(--font-title)", fontWeight: "700", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "3px", flex: "none" },
  }, [
    el("span", { text: card.sym }),
    ...(hasSibling ? [el("span", { text: "•", style: { color: "var(--text-secondary-faint)", fontSize: "13px" } })] : []),
  ]);
  // Dropdown arrow (left of the ticker) — shown only when this security's chart
  // is open. Toggles the strategy sub-cards below. Styled like the card's other
  // icons; only its shadow is a hover effect. Points down when the list is open.
  if (chartOpen) {
    const stratToggle = el("button", {
      attrs: { title: strategiesOpen ? `Hide ${card.sym} strategies` : `Show ${card.sym} strategies`, "aria-label": "Toggle strategies" },
      style: { display: "flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", flex: "none", borderRadius: "5px", color: "var(--text-secondary)", cursor: "pointer", transition: "box-shadow .12s ease" },
      onClick: (e) => { e.stopPropagation(); handlers.onToggleStrategies(); },
    }, [svgIcon(icons.chevron, 12)]);
    (stratToggle.firstElementChild as HTMLElement).style.transform = strategiesOpen ? "rotate(0deg)" : "rotate(-90deg)";
    (stratToggle.firstElementChild as HTMLElement).style.transition = "transform .12s ease";
    stratToggle.addEventListener("pointerenter", () => { stratToggle.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"; });
    stratToggle.addEventListener("pointerleave", () => { stratToggle.style.boxShadow = "none"; });
    header.appendChild(stratToggle);
  }
  header.appendChild(nameBtn);

  // (Trade-type status icons removed — strategies are authored and shown on the
  // chart now, so the security card stays clean: just the ticker and price.)

  header.appendChild(spacer());
  // Price at rest (right-aligned, last digit under the ✕); hovering the card hides it and
  // reveals the action icons in the same spot.
  if (price != null) {
    header.appendChild(
      el("div", {
        className: "wl-rest-only",
        text: price.toFixed(2),
        style: { position: "absolute", right: "4px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", font: "var(--font-mono-value)", color: "rgba(154,154,158,0.85)", whiteSpace: "nowrap", textAlign: "right" },
      })
    );
  }

  // Corner controls — hover-revealed at rest. The pin keeps a card expanded across
  // navigation; it's only meaningful while expanded, so it's shown only then (replacing
  // the old padlock — no more copy-me / multi-select checkbox).
  const corner = el("div", { style: { display: "flex", alignItems: "center", gap: "3px", flex: "none" } });
  // (Settings gear removed — configuration now lives in the Build side-panel
  // strategy cards; the retired Setups/Build/Confirm overlay is no longer reachable.)
  const pinBtn = iconButton(icons.pin, { label: locked ? `Unpin ${card.sym}` : `Pin ${card.sym} open`, size: 11, color: locked ? "var(--accent-blue)" : undefined, fill: locked, onClick: handlers.onToggleLock });
  // Discord (customer-facing) retired — D-131; no per-row Discord toggle.
  const bellBtn = iconButton(hasAlert ? icons.bellFill : icons.bell, { label: `${card.sym} alert`, size: 9, color: hasAlert ? "var(--accent-yellow)" : undefined, fill: hasAlert, onClick: handlers.onAlert });
  const closeBtn = closeButton(handlers.onRemove, { label: "Remove this card", size: 11 });
  Object.assign(closeBtn.style, { width: "18px", height: "18px" });
  // Add a strategy to this security's list (only while the chart is open — same
  // gate as the dropdown; same hover-reveal as the other icons).
  const addStratBtn = chartOpen ? iconButton(icons.plus, { label: `Add a strategy to ${card.sym}`, size: 12, onClick: handlers.onAddStrategy }) : null;
  for (const b of [addStratBtn, bellBtn, closeBtn]) b?.classList.add("wl-hover-action");
  // Pin: hidden when collapsed; when expanded it fades in on hover, but stays visible
  // while pinned (it's a live state).
  if (collapsed) pinBtn.style.display = "none";
  else if (!locked) pinBtn.classList.add("wl-hover-action");
  if (addStratBtn) corner.append(addStratBtn);
  corner.append(bellBtn, pinBtn, closeBtn);
  header.appendChild(corner);
  block.appendChild(header);

  // The bars for the active category (computed at the call site; empty when
  // collapsed). Indicator = the card's readings; statistical/structural = one
  // reading across the timeframes; cross/inter show the placeholder below.
  for (const b of bars) {
    block.appendChild(
      el("div", { style: { display: "flex", alignItems: "center", gap: "6px", margin: "4px 0" } }, [
        // Reading + timeframe columns (timeframe centred between the reading and the low
        // word); the low/high words hug the slider, which takes the remaining width.
        ...labelCols(b.label),
        el("div", { text: b.cell.ends[0], style: { width: "38px", flex: "none", textAlign: "right", whiteSpace: "nowrap", font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
        readingTrack(b.cell.v, card.os, card.ob),
        el("div", { text: b.cell.ends[1], style: { width: "38px", flex: "none", textAlign: "left", whiteSpace: "nowrap", font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
      ])
    );
  }
  if (!collapsed && activeCat && bars.length === 0) {
    const msg = activeCat === "inter" ? "Add more instruments to your watchlist to compare." : "No data yet.";
    block.appendChild(el("div", { text: msg, style: { font: "var(--font-caption)", color: "var(--text-secondary)", padding: "10px 2px 4px", textAlign: "center" } }));
  }

  // Strategy sub-cards — full-width mini-cards stacked BELOW the security card,
  // revealed only when the dropdown arrow is open. Selecting one makes it active
  // (the chart's flask lights up and loads it); the selected one is highlighted.
  // Sub-cards carry their own hover shadow, like the security box.
  block.style.marginBottom = "0"; // the wrapper carries the gap between groups now
  const wrap = el("div", { style: { display: "flex", flexDirection: "column", marginBottom: "8px" } });
  wrap.appendChild(block);
  if (chartOpen && strategiesOpen) {
    const stack = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", margin: "6px 0 2px", paddingLeft: "16px" } });
    if (!book.strategies.length) {
      stack.appendChild(el("div", { text: "No strategies yet — tap + on the card to add one.", style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)", padding: "4px 2px" } }));
    }
    for (const s of book.strategies) {
      const on = book.activeId === s.id;
      const sub = el("div", {
        className: "wl-subcard",
        attrs: { title: on ? `Deselect ${s.name}` : `Select ${s.name}` },
        style: { display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px 8px 12px", borderRadius: "10px", cursor: "pointer", background: on ? "rgba(50,120,230,0.10)" : "var(--bg-elevated)", border: `1px solid ${on ? "var(--accent-blue)" : "var(--border)"}`, boxShadow: "none", transition: "box-shadow .12s ease" },
      });
      sub.addEventListener("pointerenter", () => { sub.style.boxShadow = "0 2px 10px rgba(0,0,0,0.12)"; });
      sub.addEventListener("pointerleave", () => { sub.style.boxShadow = "none"; });
      const dot = el("div", { style: { width: "7px", height: "7px", borderRadius: "999px", flex: "none", background: on ? "var(--accent-blue)" : "#cfcfd4" } });
      const nameEl = el("div", { text: s.name, attrs: { title: "Double-click to rename" }, style: { flex: "1", minWidth: "0", font: "var(--font-body)", fontWeight: on ? "600" : "500", color: on ? "var(--accent-blue)" : "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } });
      // Double-click the name to rename it inline; single click still selects.
      // A short timer disambiguates the two so the click's re-render doesn't
      // tear down the row before the double-click lands.
      let clickTimer: number | null = null;
      const startRename = () => {
        const input = el("input", { style: { flex: "1", minWidth: "0", font: "var(--font-body)", fontWeight: on ? "600" : "500", color: "var(--text-primary)", border: "1px solid var(--accent-blue)", borderRadius: "5px", padding: "1px 5px", background: "var(--bg)" } }) as HTMLInputElement;
        input.value = s.name;
        nameEl.replaceWith(input);
        input.focus(); input.select();
        let done = false;
        const commit = (save: boolean) => { if (done) return; done = true; const v = input.value.trim(); handlers.onRenameStrategy(s.id, save && v ? v : s.name); };
        input.addEventListener("click", (e) => e.stopPropagation());
        input.addEventListener("dblclick", (e) => e.stopPropagation());
        input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); commit(true); } else if (e.key === "Escape") { e.preventDefault(); commit(false); } });
        input.addEventListener("blur", () => commit(true));
      };
      sub.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).closest("button, input")) return;
        if (clickTimer != null) return;
        clickTimer = window.setTimeout(() => { clickTimer = null; handlers.onSelectStrategy(on ? null : s.id); }, 220);
      });
      sub.addEventListener("dblclick", (e) => {
        if ((e.target as HTMLElement).closest("button, input")) return;
        if (clickTimer != null) { clearTimeout(clickTimer); clickTimer = null; }
        startRename();
      });
      const dupBtn = iconButton(icons.copy, { label: `Duplicate ${s.name}`, size: 12, onClick: () => handlers.onDuplicateStrategy(s.id) });
      const delBtn = closeButton(() => handlers.onRemoveStrategy(s.id), { label: `Delete ${s.name}`, size: 11 });
      Object.assign(delBtn.style, { width: "18px", height: "18px" });
      sub.append(dot, nameEl, dupBtn, delBtn);
      stack.appendChild(sub);
    }
    wrap.appendChild(stack);
  }
  return wrap;
}

/** Centered modal shell used by the two card editors (matches the app's other popups). */
function modal(width: string, onClose: () => void, children: HTMLElement[]): HTMLElement {
  const overlay = el("div", {
    style: { position: "absolute", inset: "0", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)", zIndex: "10" },
    onClick: (e) => { if (e.target === overlay) onClose(); },
  });
  const card = el("div", { style: { width, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "12px", padding: "12px", display: "flex", flexDirection: "column", gap: "8px" } });
  for (const c of children) card.appendChild(c);
  overlay.appendChild(card);
  return overlay;
}

/** The flask opens the "lab" as a full-surface overlay ON THE CHART for this
 * symbol (same pattern as the builder's info/deploy overlays): focus/open the
 * chart tab, then signal it to show the lab box. Empty shell for now. */
/** Clicking a card's settings icon opens the card's settings as a full-surface
 * builder (D-129). Edits stream back via "setupLibraryAction". */
/** v1 (MVP, D-129): push this symbol's saved setups to the builder tab. Same data
 * shape and same "setupLibraryAction" write-back loop as pushSetups, but the
 * receiver is the chartless builder page (builder.ts), not the chart. Used both to
 * seed a freshly-opened builder tab and to refresh an already-open one after a
 * library action. */
function pushBuilderData(sym: string): void {
  const setups = confluenceStore.setupsForSym(sym).map((card) => ({ card, live: confluenceStore.isLive(card.id) }));
  void storageSet("setupSettings", { symbol: sym, setups, at: Date.now() });
}

/** v1 (MVP, D-129): the security-click entry point — open the Setups / Build /
 * Confirm builder as a full-window tab (no chart) and hand it this symbol's setups.
 * This is the start of v1's end-to-end flow: connect → build → test → deploy →
 * monitor. */
function openBuilderTab(sym: string): void {
  platform.openBuilder(sym); // platform seam (D-133) → opens a tab in the extension, a route in the web shell
  pushBuilderData(sym);
}


/** Modal price-alert editor, ported from PriceAlertPopup. */
function priceAlertPopup(
  symbol: string,
  currentPrice: number,
  existingTarget: number | null,
  actions: { onConfirm: (price: number) => void; onRemove: () => void; onClose: () => void }
): HTMLElement {
  let priceValue = existingTarget ?? currentPrice;
  const field = el("input", { attrs: { type: "text", inputmode: "decimal" }, style: { textAlign: "center", font: "var(--font-stat-value)", flex: "1", minWidth: "0" } }) as HTMLInputElement;
  field.value = priceValue.toFixed(2);
  const step = () => (priceValue >= 1000 ? 10 : priceValue >= 100 ? 1 : priceValue >= 10 ? 0.1 : 0.01);
  const commitText = () => {
    const parsed = Number(field.value);
    if (Number.isFinite(parsed) && parsed > 0) priceValue = Math.round(parsed * 100) / 100;
    field.value = priceValue.toFixed(2);
  };
  const adjust = (delta: number) => { commitText(); priceValue = Math.max(0, Math.round((priceValue + delta) * 100) / 100); field.value = priceValue.toFixed(2); };
  field.addEventListener("focus", () => { field.value = ""; });
  field.addEventListener("blur", commitText);
  field.addEventListener("keydown", (e) => { if (e.key === "Enter") commitText(); });

  const buttonRow = el("div", { style: { display: "flex", alignItems: "center" } });
  if (existingTarget != null) buttonRow.appendChild(el("button", { text: "Remove Alert", style: { font: "var(--font-body)", color: "var(--accent-red)" }, onClick: actions.onRemove }));
  buttonRow.appendChild(spacer());
  buttonRow.appendChild(el("button", { text: "Set Alert", style: { font: "var(--font-button)", padding: "5px 12px", borderRadius: "999px", background: "var(--bg-fill-strong)" }, onClick: () => { commitText(); actions.onConfirm(priceValue); } }));

  return modal("220px", actions.onClose, [
    el("div", { style: { display: "flex", alignItems: "center" } }, [el("div", { text: `${symbol} alert`, style: { font: "var(--font-title)" } }), spacer(), closeButton(actions.onClose, { label: "Close" })]),
    el("div", { text: "Notify me when price crosses:", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
    el("div", { style: { display: "flex", alignItems: "center", gap: "8px" } }, [
      el("button", { text: "−", style: { fontSize: "16px", color: "var(--text-secondary)" }, onClick: () => adjust(-step()) }),
      field,
      el("button", { text: "+", style: { fontSize: "16px", color: "var(--text-secondary)" }, onClick: () => adjust(step()) }),
    ]),
    buttonRow,
  ]);
}

export const watchlistStore = new WatchlistStore();
let storeInitialized = false;
// Long-press (select-all) timer, module-scoped so a re-render can cancel a
// pending press — otherwise a rebuild between pointerdown and pointerup would
// let the timer fire and turn a normal tap into a select-all.
let lpTimer: ReturnType<typeof setTimeout> | null = null;
let lpFired = false;
let dragId: string | null = null; // card being dragged (reorder)

export async function renderWatchlistPage(root: HTMLElement): Promise<() => void> {
  void stockDirectory.ensureLoaded();
  if (!storeInitialized) {
    storeInitialized = true;
    void watchlistStore.init();
    void confluenceStore.init();
  }
  await journalStore.init();
  await confluenceData.load(); // last-known candles → readings show instantly on open

  let selectedFilter: AssetClass | null = null;
  // Asset classes the current data mode allows (D-152). Free = Stocks; Premium =
  // union of connected providers' classes. Refreshed on mount, on mode change,
  // and when the search opens (so a just-connected feed shows up).
  let availClasses: AssetClass[] = ["Stocks"];
  async function refreshAvail(): Promise<void> {
    availClasses = await availableClasses();
    if (selectedFilter && !availClasses.includes(selectedFilter)) selectedFilter = null;
    render();
  }
  let highlightedIndex = -1;
  let addError: string | null = null;
  // Build strategy editor (LAB): when a security is opened, the Build panel shows
  // its strategy sub-box stack instead of the watchlist. Owns its own DOM.
  let strategyViewSym: string | null = null;
  let strategyView: { el: HTMLElement; dispose: () => void } | null = null;
  let alertEditingSymbol: string | null = null;
  // Cards default to collapsed. `lockedIds` (persisted) are the ones the user has
  // pinned open — they survive navigating away; everything else re-collapses because
  // `sessionExpanded` is not persisted and starts empty on each mount.
  const lockedIds = new Set<string>(await storageGet<string[]>("wlLocked", []));
  // Per-card relational tuning (cross-sectional lookback; intermarket window / mode / max names).
  const wlRelSettings = await storageGet<Record<string, RelSettings>>("wlRelSettings", {});
  const relOf = (id: string): RelSettings => ({ ...DEFAULT_REL, ...(wlRelSettings[id] ?? {}) });
  const sessionExpanded = new Set<string>();
  // Which cards have their strategy sub-card dropdown open (session-only).
  const stratOpen = new Set<string>();
  // Cards no longer expand (D-118): the pills are pure status indicators and all
  // configuration lives in the Settings overlay (opened by the corner gear).
  const isExpanded = (_id: string): boolean => false;
  // Which instrument-agnostic category each card is currently showing (session-only,
  // like sessionExpanded — resets to the default on navigate-away).
  const cardCategory = new Map<string, Cat>();
  // Which trade-type setup is the ACTIVE one per symbol (session-only) — the pill
  // whose board shows when the security card is expanded. Defaults to the first.
  const activeType = new Map<string, string>();
  // The card whose settings overlay is currently open on the chart (opening a setup
  // opens its settings; closing either side closes the other) — D-114.
  let settingsOpenCardId: string | null = null;
  // Open the settings overlay for a card on a given lens, and remember it's open.
  // Close the settings overlay on the chart (if this card's is the open one).
  const closeSettingsFor = (id: string): void => { if (settingsOpenCardId === id) { settingsOpenCardId = null; void storageSet("chartCardSettings", null); } };
  // Each security's book of strategies (loaded lazily, cached so the synchronous
  // render can read it). saveBook writes the shared storage key the chart also
  // watches, so selecting/adding here re-syncs an open chart.
  const books = new Map<string, StudioBook>();
  const BOOK_PREFIX = bookStorageKey("");
  let booksLoading = false;
  const getBook = (sym: string): StudioBook => books.get(sym) ?? { strategies: [], activeId: null };
  async function ensureBooks(syms: string[]): Promise<void> {
    const missing = [...new Set(syms)].filter((s) => !books.has(s));
    if (!missing.length || booksLoading) return;
    booksLoading = true;
    for (const s of missing) books.set(s, await loadBook(s));
    booksLoading = false;
    render();
  }
  function mutateBook(sym: string, fn: (b: StudioBook) => void): void {
    const b = getBook(sym);
    fn(b);
    books.set(sym, b);
    void saveBook(sym, b);
    render();
  }
  // The symbol whose chart tab is currently ACTIVE (the tab being viewed) — only its card is tinted.
  let activeChartSym: string | null = null;
  const refreshActiveChart = async () => {
    try {
      const map = await storageGet<Record<string, OpenChartTab>>("openChartTabs", {});
      const tabs = await chrome.tabs.query({ active: true, lastFocusedWindow: true });
      const id = tabs[0]?.id;
      activeChartSym = id != null ? (map[String(id)]?.symbol ?? null) : null;
    } catch { activeChartSym = null; }
  };
  // Toolbar: which control (if any) is revealed in the strip below the toolbar.
  let revealMode: "search" | null = null;
  // Watchlists (D-121): the board icon opens a LIST of watchlists (cards) instead
  // of a dropdown. listView = showing that list; false = the active watchlist's
  // securities. rename/delete are inline states on a watchlist card.
  let listView = false;
  let renameName: string | null = null;
  let deleteName: string | null = null;
  let wlDrag: string | null = null; // watchlist name being drag-reordered on the list page
  let headerEditing = false;        // renaming the active watchlist inline from its header


  const container = el("div", { style: { display: "flex", flexDirection: "column", gap: "8px", height: "100%", position: "relative" } });
  root.replaceChildren(container);

  const input = el("input", { attrs: { type: "text", placeholder: "Add ticker or company name" }, style: { width: "100%", minWidth: "0", paddingRight: "28px" } }) as HTMLInputElement;
  const clearBtn = closeButton(() => { input.value = ""; addError = null; highlightedIndex = -1; input.focus(); render(); }, { title: "Clear" });
  Object.assign(clearBtn.style, { position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", width: "20px", height: "20px", display: "none" });
  const inputWrap = el("div", { style: { position: "relative", flex: "1", minWidth: "0" } }, [input, clearBtn]);

  function persistWorking(markDirty = true) {
    void confluenceStore.setWorking(confluenceStore.working, markDirty);
  }

  function addTicker(raw?: string) {
    const ticker = stockDirectory.resolveTicker(raw ?? input.value);
    if (!ticker) { addError = "Not found"; render(); return; }
    // Gate by data mode (D-152): only classes you can actually get data for.
    const cls = classifySymbol(ticker, stockDirectory.category(ticker) ?? null);
    if (!availClasses.includes(cls)) {
      addError = availClasses.length
        ? `${cls} needs a data connection — connect one in Setup, or switch to Free.`
        : "Connect a data feed in Setup, or switch to Free, to add instruments.";
      render();
      return;
    }
    addError = null;
    void confluenceStore.addSecurity(ticker);
    input.value = "";
    highlightedIndex = -1;
    revealMode = null; // selecting a result closes the search dropdown
    render();
  }
  function confirmSelection() {
    const matches = stockDirectory.suggestions(input.value, selectedFilter, 200).filter((s) => availClasses.includes(s.category));
    if (highlightedIndex >= 0 && highlightedIndex < matches.length) addTicker(matches[highlightedIndex]!.ticker);
    else addTicker();
  }
  function removeCard(card: Card) {
    // Remove the whole security (every card for the symbol) in one click, plus its
    // per-symbol alert + Discord. removeSymbol is async — re-render AFTER it resolves
    // so the card is actually gone on the first click (not a tick later).
    void confluenceStore.removeSymbol(card.sym).then(() => render());
    void watchlistStore.removeAlert(card.sym);
  }

  input.addEventListener("input", () => { addError = null; highlightedIndex = -1; render(); });
  input.addEventListener("keydown", (e) => {
    const matches = stockDirectory.suggestions(input.value, selectedFilter, 200).filter((s) => availClasses.includes(s.category));
    if (e.key === "ArrowDown" && matches.length) { highlightedIndex = Math.min(highlightedIndex + 1, matches.length - 1); e.preventDefault(); render(); }
    else if (e.key === "ArrowUp" && matches.length) { highlightedIndex = Math.max(highlightedIndex - 1, 0); e.preventDefault(); render(); }
    else if (e.key === "Enter") confirmSelection();
    else if (e.key === "Escape") { revealMode = null; input.blur(); render(); }
  });

  // Open a watchlist (make it active) and return to the securities view. Auto-save
  // means there's nothing to commit first (D-121).
  function openWatchlist(name: string) {
    void confluenceStore.switchTo(name);
    listView = false; renameName = deleteName = null;
    render();
  }

  // ---- Watchlists list page — cards, one per saved watchlist (D-121) --------
  function watchlistsListView(): HTMLElement {
    const wrap = el("div", { className: "scroll", style: { flex: "1", overflowX: "hidden", overflowY: "auto", scrollbarGutter: "stable both-edges", display: "flex", flexDirection: "column", gap: "6px", padding: "2px" } });
    wrap.appendChild(el("div", { text: "Watchlists", style: { font: "var(--font-title)", textAlign: "center", padding: "2px 0 6px" } }));
    // Manual order (D-121): keep the stored key order (drag-and-drop rewrites it),
    // don't alphabetise. `wlDrag` is the name being dragged this gesture.
    const names = Object.keys(confluenceStore.boards);
    for (const nm of names) {
      const board = confluenceStore.boards[nm] ?? [];
      const secCount = new Set(board.map((c) => c.sym)).size;
      const isCurrent = nm === confluenceStore.current;
      const item = el("div", { className: "wl-list-card", style: { border: `1px solid ${isCurrent ? "var(--accent-blue)" : "var(--border)"}`, borderRadius: "10px", padding: "10px 12px", cursor: "pointer" } });
      // Hover lift — the same soft drop shadow the security / Tune cards use.
      item.addEventListener("pointerenter", () => { if (!wlDrag) item.style.boxShadow = "0 2px 10px rgba(0,0,0,0.12)"; });
      item.addEventListener("pointerleave", () => { if (!wlDrag) item.style.boxShadow = ""; });
      // Drag to reorder (like the securities within a watchlist). Disabled while
      // this card is mid-rename / mid-delete so the input/buttons stay usable.
      if (renameName !== nm && deleteName !== nm) {
        item.draggable = true;
        item.addEventListener("dragstart", (e) => { wlDrag = nm; item.style.opacity = "0.5"; if (e.dataTransfer) e.dataTransfer.effectAllowed = "move"; });
        item.addEventListener("dragend", () => { wlDrag = null; item.style.opacity = "1"; item.style.boxShadow = ""; });
        item.addEventListener("dragover", (e) => {
          e.preventDefault();
          if (!wlDrag || wlDrag === nm) return;
          // Blue insert line above/below, matching the securities reorder cue.
          const r = item.getBoundingClientRect();
          const after = (e.clientY - r.top) > r.height / 2;
          item.style.boxShadow = after ? "inset 0 -2px 0 var(--accent-blue)" : "inset 0 2px 0 var(--accent-blue)";
        });
        item.addEventListener("dragleave", () => { item.style.boxShadow = ""; });
        item.addEventListener("drop", (e) => {
          e.preventDefault();
          item.style.boxShadow = "";
          if (!wlDrag || wlDrag === nm) return;
          const r = item.getBoundingClientRect();
          const after = (e.clientY - r.top) > r.height / 2;
          const from = wlDrag; wlDrag = null;
          void confluenceStore.reorderBoard(from, nm, after).then(() => render());
        });
      }

      if (deleteName === nm) {
        item.appendChild(el("div", { text: `Delete “${nm}”? This removes the whole watchlist.`, style: { font: "var(--font-caption)", color: "var(--text-secondary)", marginBottom: "8px" } }));
        item.appendChild(el("div", { style: { display: "flex", gap: "8px", justifyContent: "flex-end" } }, [
          el("button", { text: "Cancel", style: { font: "var(--font-caption)", border: "1px solid var(--border)", background: "var(--bg)", borderRadius: "7px", padding: "5px 12px", cursor: "pointer" }, onClick: () => { deleteName = null; render(); } }),
          el("button", { text: "Delete", style: { font: "var(--font-caption)", color: "#fff", background: "var(--accent-red)", border: "0", borderRadius: "7px", padding: "5px 12px", cursor: "pointer" }, onClick: () => { void confluenceStore.deleteBoard(nm).then(() => { deleteName = null; render(); }); } }),
        ]));
        wrap.appendChild(item); continue;
      }

      const row = el("div", { style: { display: "flex", alignItems: "center", gap: "10px" } });
      if (renameName === nm) {
        const ri = el("input", { attrs: { type: "text" }, style: { flex: "1", minWidth: "0", font: "var(--font-body)" } }) as HTMLInputElement;
        ri.value = nm;
        const commitR = () => { const raw = ri.value.trim(); if (raw && raw !== nm) void confluenceStore.renameBoard(nm, raw).then(() => { renameName = null; render(); }); else { renameName = null; render(); } };
        ri.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); commitR(); } else if (e.key === "Escape") { renameName = null; render(); } });
        row.append(ri, el("button", { text: "✓", style: { width: "30px", flex: "none", background: "var(--accent-blue)", color: "#fff", border: "0", borderRadius: "7px", cursor: "pointer" }, onClick: commitR }));
        setTimeout(() => ri.focus(), 0);
      } else {
        const mid = el("div", { style: { flex: "1", minWidth: "0", cursor: "pointer" } }, [
          el("div", { text: nm, style: { font: "var(--font-header)", color: isCurrent ? "var(--accent-blue)" : "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
          el("div", { text: `${secCount} ${secCount === 1 ? "security" : "securities"}${isCurrent ? " · open" : ""}`, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
        ]);
        mid.addEventListener("click", () => openWatchlist(nm));
        const rename = el("button", { text: "✎", attrs: { title: "Rename" }, style: { flex: "none", background: "none", border: "0", color: "var(--text-secondary)", cursor: "pointer", padding: "3px 5px" }, onClick: () => { renameName = nm; deleteName = null; render(); } });
        const dup = iconButton(icons.copy, { label: `Duplicate ${nm}`, size: 13, onClick: () => { void confluenceStore.duplicateBoard(nm).then(() => render()); } });
        const del = el("button", { text: "✕", attrs: { title: "Delete" }, style: { flex: "none", background: "none", border: "0", color: "var(--accent-red)", cursor: "pointer", fontWeight: "600", padding: "3px 5px" }, onClick: () => { deleteName = nm; renameName = null; render(); } });
        row.append(mid, rename, dup, del);
      }
      item.appendChild(row);
      wrap.appendChild(item);
    }
    // Create new watchlist — the same circular tool icon (grey bubble on hover), not a dashed box.
    const createBtn = pillButton(icons.plus, "Create new watchlist", () => { void confluenceStore.newWatchlist().then(() => { listView = false; render(); }); }, {});
    wrap.appendChild(el("div", { style: { display: "flex", justifyContent: "center", marginTop: "4px" } }, [createBtn]));
    return wrap;
  }

  function render() {
    // Build strategy editor takes over the whole panel for the opened security.
    // It manages its own DOM, so timer-driven re-renders here don't touch it.
    if (strategyViewSym) {
      if (!strategyView) {
        strategyView = renderStrategyView(strategyViewSym, {
          onBack: () => { strategyView?.dispose(); strategyView = null; strategyViewSym = null; render(); },
        });
      }
      if (root.firstChild !== strategyView.el) root.replaceChildren(strategyView.el);
      return;
    }
    if (strategyView) { strategyView.dispose(); strategyView = null; }
    if (root.firstChild !== container) root.replaceChildren(container);

    const hadFocus = document.activeElement === input;
    const selStart = input.selectionStart;
    const selEnd = input.selectionEnd;
    if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; } // a rebuild cancels any pending long-press
    container.replaceChildren();

    const board = confluenceStore.working;
    void ensureBooks(board.map((c) => c.sym)); // load each security's strategy book (re-renders when ready)
    // Expanded cards are what the eye is on — refresh their info first and on a
    // tighter clock (~1 min); collapsed cards ride the normal TTL (D-094).
    const prioritySyms = new Set(board.filter((c) => isExpanded(c.id)).map((c) => c.sym));
    void confluenceData.ensure(board, { prioritySyms, priorityMaxAgeMs: 60_000 });

    const pill = (label: string, isSelected: boolean, onClick: () => void) => {
      const btn = el("button", { className: "pill" + (isSelected ? " selected" : ""), text: label, onClick });
      btn.style.padding = "4px 8px";
      return btn;
    };

    // Toolbar. On the Watchlists list page it's just a + (create new watchlist).
    // Inside a watchlist: search (add a security) + the board icon (→ the list).
    // `scrollbar-gutter` (with overflow set) reserves the same side gutters the
    // scrolling card list reserves, so the toolbar's edges — and the inline rename
    // box — line up with the cards below.
    const toolbar = el("div", { className: "wl-toolbar", style: { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", flex: "none", overflow: "hidden", scrollbarGutter: "stable both-edges" } });
    if (!listView) {
      // Inside a watchlist: back arrow + the watchlist name on the left, search on
      // the right. Click the name to rename it in place. The watchlists list page
      // (create / rename / copy) is reached via this back button.
      toolbar.appendChild(pillButton(icons.back, "Back to watchlists", () => { listView = true; revealMode = null; renameName = deleteName = null; headerEditing = false; render(); }, {}));
      const cur = confluenceStore.current;
      if (cur && headerEditing) {
        const ri = el("input", { attrs: { type: "text" }, style: { flex: "1", minWidth: "0", font: "var(--font-header)" } }) as HTMLInputElement;
        ri.value = cur;
        const commit = () => { const raw = ri.value.trim(); headerEditing = false; if (raw && raw !== cur) void confluenceStore.renameBoard(cur, raw).then(() => render()); else render(); };
        ri.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } else if (e.key === "Escape") { headerEditing = false; render(); } });
        ri.addEventListener("blur", commit);
        toolbar.appendChild(ri);
        setTimeout(() => { ri.focus(); ri.select(); }, 0);
      } else {
        if (cur) { const nameEl = el("div", { text: cur, attrs: { title: "Rename watchlist" }, style: { font: "var(--font-header)", fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: "0", cursor: "pointer" } }); nameEl.addEventListener("click", () => { headerEditing = true; render(); }); toolbar.appendChild(nameEl); }
        toolbar.appendChild(el("div", { style: { flex: "1" } })); // spacer → search sits on the right
        toolbar.appendChild(pillButton(icons.search, "Add a security", () => { const opening = revealMode !== "search"; revealMode = opening ? "search" : null; if (opening) void refreshAvail(); else render(); }, { active: revealMode === "search" }));
        // Scan (D-158): build one setup and arm it live across EVERY security in this
        // watchlist, so each notifies in Monitor on its own — like arming individually.
        toolbar.appendChild(pillButton(icons.scanner, "Scan the whole watchlist with one setup", () => {
          const syms = confluenceStore.distinctSyms();
          if (!syms.length) { addError = "Add a security first — a scan runs across the whole watchlist."; revealMode = "search"; void refreshAvail(); return; }
          // Persist the one-shot flag BEFORE opening so the builder (a new tab in the
          // extension, a route in the web shell) reliably reads it and opens on a fresh setup.
          void storageSet("scanBuildNew", { at: Date.now() }).then(() => openBuilderTab(syms[0]!));
        }, { active: confluenceStore.hasScanSetups() }));
        // When a scan is armed, offer a one-click clear from every security.
        if (confluenceStore.hasScanSetups()) {
          toolbar.appendChild(pillButton(icons.close, "Clear the scan from every security", () => { void confluenceStore.clearScanSetups().then(() => render()); }, {}));
        }
      }
    }
    // No manual refresh button (D-094) — Watch auto-refreshes every minute.
    container.appendChild(toolbar);

    // Watchlists list page: cards = watchlists; return here after opening one.
    if (listView) { container.appendChild(watchlistsListView()); return; }

    // Reveal strip — the toggled search box (inside a watchlist).
    if (revealMode === "search") {
      const wrap = el("div", { className: "wl-reveal", style: { position: "relative" } });
      wrap.appendChild(inputWrap);
      clearBtn.style.display = input.value ? "flex" : "none";
      if (addError) wrap.appendChild(el("div", { text: addError, style: { font: "var(--font-caption)", color: "var(--accent-orange)", paddingTop: "4px" } }));
      const matches = stockDirectory.suggestions(input.value, selectedFilter, 200).filter((s) => availClasses.includes(s.category));
      const dropdown = el("div", { style: { position: "absolute", top: "34px", left: "0", right: "0", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "8px", padding: "4px", zIndex: "5" } });
      const filterBar = el("div", { style: { display: "flex", gap: "4px", flexWrap: "wrap", padding: "2px 4px 6px" } });
      if (availClasses.length === 0) {
        filterBar.appendChild(el("div", { text: "No data connections yet — connect one in Setup, or switch to Free mode.", style: { font: "var(--font-caption)", color: "var(--text-secondary)", padding: "2px 4px" } }));
      } else {
        for (const cls of availClasses) filterBar.appendChild(pill(cls, selectedFilter === cls, () => ((selectedFilter = selectedFilter === cls ? null : cls), render())));
      }
      dropdown.appendChild(filterBar);
      if (matches.length) {
        const dl = el("div", { className: "scroll", style: { maxHeight: "249px", overflowY: "auto" } });
        matches.forEach((stock, index) => {
          dl.appendChild(el("button", { className: "wl-menu-item" + (index === highlightedIndex ? " sel" : ""), style: { display: "flex", gap: "8px", alignItems: "center", width: "100%", padding: "6px 8px", textAlign: "left" }, onClick: () => addTicker(stock.ticker) }, [
            el("div", { text: stock.ticker, style: { font: "var(--font-header)", width: "72px", flex: "none", whiteSpace: "nowrap" } }),
            el("div", { text: stock.name, style: { font: "var(--font-body)", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
          ]));
        });
        if (highlightedIndex >= 0) dl.children[highlightedIndex]?.scrollIntoView({ block: "nearest" });
        dropdown.appendChild(dl);
      }
      wrap.appendChild(dropdown);
      container.appendChild(wrap);
      setTimeout(() => input.focus(), 0);
    }


    // While the search dropdown is open, dim the existing cards (keep context,
    // shift focus to the search) — and stop clicks landing on the dimmed list.
    const searching = revealMode === "search";
    const list = el("div", { className: "scroll", style: { flex: "1", overflowX: "hidden", overflowY: "auto", scrollbarGutter: "stable both-edges", opacity: searching ? "0.35" : "1", pointerEvents: searching ? "none" : "auto", transition: "opacity 0.12s ease" } });
    // Group the board by symbol — a symbol's cards are already adjacent (normalizeBoard).
    // One security card per group; its trade-type setups become pills, and the ACTIVE
    // setup's reading board shows when the card is expanded (D-114 increment 2).
    const symOrder: string[] = [];
    const symGroups = new Map<string, Card[]>();
    for (const c of board) { let g = symGroups.get(c.sym); if (!g) { g = []; symGroups.set(c.sym, g); symOrder.push(c.sym); } g.push(c); }
    const activeCardOf = (sym: string): Card => { const g = symGroups.get(sym)!; return g.find((c) => c.id === activeType.get(sym)) ?? g[0]!; };
    const litSyms = new Set<string>(); // highlight just one card per open chart
    if (symOrder.some((s) => isExpanded(activeCardOf(s).id))) list.appendChild(boardHeader()); // only when a card is showing sliders
    for (const sym of symOrder) {
      const group = symGroups.get(sym)!;
      const card = activeCardOf(sym);
      const snap = watchlistStore.snapshots[card.sym];
      const cells = confluenceData.cellsFor(card);
      const cat: Cat | null = isExpanded(card.id) ? (cardCategory.get(card.id) ?? "indicator") : null;
      let bars: Bar[] = [];
      if (cat === "indicator") {
        bars = lenses(card).map((l, i) => ({ label: l.label, cell: cells[i]! }));
      } else if (cat === "statistical" || cat === "structural") {
        const reading = card.catReading?.[cat] ?? CAT_READING[cat]!;
        const lookback = card.catLookback?.[cat];
        const tfs = [...new Set(card.cols.map((c) => c.tf))];
        bars = tfs.map((tf) => ({ label: tf, cell: confluenceData.cellForReading(card.sym, reading, tf, { lookback }) }));
      } else if (cat === "cross") {
        bars = confluenceData.crossBars(board, card.sym, RELATIONAL_TF, relOf(card.id).xLookback, relOf(card.id).xMeasures);
      } else if (cat === "inter") {
        const r = relOf(card.id);
        bars = confluenceData.interBars(board, card.sym, RELATIONAL_TF, { window: r.iWindow, mode: r.iMode, maxNames: r.iMax, minAbs: r.iMinAbs });
      }
      const chartOpen = card.sym === activeChartSym && !litSyms.has(card.sym);
      if (chartOpen) litSyms.add(card.sym);
      // Pills = the four named trade-type setups (the security's live one, or the
      // starter library evaluated live) plus the relational lenses. Custom / unnamed
      // setups no longer get a pill (D-118) — they live in the Setups library only.
      const vOf = (c: Card): Verdict => !confluenceData.filtersPass(c) ? null
        : c.engine === "breakout" ? confluenceData.breakoutVerdictFor(c)
        : verdict(confluenceData.cellsFor(c).map((x) => x.v), c);
      // A card setup's ring means ITS OWN readings are on screen — so it's off while a
      // relational lens (cross / inter) is showing, keeping exactly one pill ringed.
      const activeOf = (c: Card): boolean => c.id === card.id && isExpanded(card.id) && cat !== "cross" && cat !== "inter";
      // Only trade types the user has made LIVE (ticked in the Setups library) get an
      // icon (D-118) — no more "show every type" ephemeral pills. In library order.
      const namedPills: PillSpec[] = [];
      for (const t of TRADE_TYPES) {
        const ow = group.find((c) => c.name === t.name);
        if (ow) namedPills.push({ id: ow.id, kind: "card", label: t.pill, v: vOf(ow), dir: ow.dir, active: activeOf(ow) });
      }
      // Relational setups (D-114 increment 3) — computed from the live cross-sectional
      // rank; they open the relational lens rather than a card. Cross only for now.
      const relPills: PillSpec[] = RELATIONAL_TYPES.map((rt) => {
        const r = relOf(card.id);
        let v: Verdict = null;
        if (rt.cat === "cross") {
          v = relationalVerdict(confluenceData.crossBars(board, sym, RELATIONAL_TF, r.xLookback, r.xMeasures));
        } else if (rt.cat === "inter") {
          // Spread above its partner (z ≥ +thr) → high/purple; below (z ≤ −thr) → low/blue.
          const d = confluenceData.divergence(board, sym, RELATIONAL_TF, { window: r.iWindow });
          v = d && d.z != null ? (d.z >= DIVERGENCE_Z ? "sell" : d.z <= -DIVERGENCE_Z ? "buy" : null) : null;
        }
        return { id: rt.id, kind: "rel", label: rt.pill, v, dir: undefined, active: cardCategory.get(card.id) === rt.cat && isExpanded(card.id) };
      });
      const pills: PillSpec[] = [...namedPills, ...relPills];
      list.appendChild(
        cardBlock(card, cells, snap?.currentPrice, watchlistStore.alerts[card.sym] != null, false, false, chartOpen, !isExpanded(card.id), lockedIds.has(card.id), cat, bars, {
          onChart: () => {
            // v1 (MVP, D-129): clicking a security opens the Setups / Build / Confirm
            // strategy builder as a full-window tab — no chart. This is the whole v1
            // pitch: connect → build → test → deploy → monitor, end to end, without the
            // chart surface. (v2 keeps the chart; v3 is the full app.)
            void storageSet("activeInstrument", { symbol: card.sym, interval: "1d", isIntraday: false });
            activeChartSym = card.sym;
            openBuilderTab(card.sym);
          },
          onCategory: (clicked) => {
            const expanded = isExpanded(card.id);
            const current = expanded ? (cardCategory.get(card.id) ?? "indicator") : null;
            if (expanded && current === clicked) {
              if (lockedIds.has(card.id)) return; // pinned cards can't collapse — unpin first
              sessionExpanded.delete(card.id);
            } else {
              cardCategory.set(card.id, clicked);
              if (!expanded) sessionExpanded.add(card.id);
              // If this card's settings overlay is open on the chart, switch it too
              // (the chart ignores this unless an overlay is actually open).
              void storageSet("chartCardCategorySync", { symbol: card.sym, category: clicked, at: Date.now() });
            }
            render();
          },
          // D-118: card-backed pills open the new instrument-scoped Setups overlay
          // (Setups / Build / Confirm). The relational lenses (leader/laggard,
          // divergence) aren't card-backed, so they keep their existing settings.
          onOpenSettings: () => { revealMode = null; openBuilderTab(card.sym); },
          onAlert: () => { revealMode = null; alertEditingSymbol = card.sym; render(); },
          onRemove: () => removeCard(card),
          onToggleCollapse: () => {
            if (lockedIds.has(card.id)) return; // pinned cards can't collapse — unpin first
            if (isExpanded(card.id)) { sessionExpanded.delete(card.id); closeSettingsFor(card.id); } else sessionExpanded.add(card.id);
            render();
          },
          // Pin toggle: keep this card expanded across navigation (persisted in wlLocked).
          onToggleLock: () => {
            if (lockedIds.has(card.id)) { lockedIds.delete(card.id); sessionExpanded.add(card.id); } // unpin, stay open this session
            else { lockedIds.add(card.id); sessionExpanded.add(card.id); } // pin open + persist
            void storageSet("wlLocked", [...lockedIds]);
            render();
          },
          onReorder: (draggedId, targetId, after) => {
            const arr = confluenceStore.working;
            const from = arr.findIndex((c) => c.id === draggedId);
            if (from < 0) return;
            const [moved] = arr.splice(from, 1);
            let to = arr.findIndex((c) => c.id === targetId);
            if (to < 0) { arr.splice(from, 0, moved!); return; }
            if (after) to += 1;
            arr.splice(to, 0, moved!);
            persistWorking();
            render();
          },
          onAddStrategy: () => { stratOpen.add(card.sym); mutateBook(card.sym, (b) => { addStrategy(b); }); },
          onSelectStrategy: (id) => mutateBook(card.sym, (b) => setActiveStrategy(b, id)),
          onDuplicateStrategy: (id) => mutateBook(card.sym, (b) => { duplicateStrategy(b, id); }),
          onRemoveStrategy: (id) => mutateBook(card.sym, (b) => removeStrategy(b, id)),
          onRenameStrategy: (id, name) => mutateBook(card.sym, (b) => { const st = b.strategies.find((x) => x.id === id); if (st) st.name = name; }),
          onToggleStrategies: () => {
            if (stratOpen.has(card.sym)) { stratOpen.delete(card.sym); mutateBook(card.sym, (b) => setActiveStrategy(b, null)); } // closing deselects all
            else { stratOpen.add(card.sym); render(); }
          },
        }, pills, () => { /* pills are pure status indicators (D-118) — no click behaviour; the corner gear opens Settings */ }, getBook(card.sym), stratOpen.has(card.sym))
      );
    }
    if (!board.length) {
      list.appendChild(
        el("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", minHeight: "160px", textAlign: "center", padding: "0 20px" } }, [
          el("div", { text: `“${confluenceStore.current ?? "This watchlist"}” is empty`, style: { font: "var(--font-body)", color: "var(--text-secondary)" } }),
          el("div", { text: "Tap the search icon above and type a ticker or company name — GC=F (gold futures), ES=F, SPY, AAPL all work. Each becomes a card you can tune. The back arrow takes you to your watchlists.", style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
        ])
      );
    }
    container.appendChild(list);

    if (hadFocus || revealMode === "search") { input.focus(); if (selStart != null && selEnd != null) input.setSelectionRange(selStart, selEnd); }

    // Alert popup (per symbol)
    if (alertEditingSymbol) {
      const symbol = alertEditingSymbol;
      const current = watchlistStore.snapshots[symbol]?.currentPrice ?? 0;
      container.appendChild(priceAlertPopup(symbol, current, watchlistStore.alerts[symbol]?.targetPrice ?? null, {
        onConfirm: (price) => { void watchlistStore.setAlert(symbol, price); alertEditingSymbol = null; render(); },
        onRemove: () => { void watchlistStore.removeAlert(symbol); alertEditingSymbol = null; render(); },
        onClose: () => { alertEditingSymbol = null; render(); },
      }));
    }

  }

  await refreshActiveChart();
  availClasses = await availableClasses(); // D-152: seed before first render
  const offDataMode = onDataModeChange(() => void refreshAvail());
  render();
  // Click away from an open reveal (search results / boards panel) closes it.
  // Clicks on the toolbar toggles or inside the reveal itself are left alone.
  const onDocPointerDown = (e: Event) => {
    if (revealMode === null) return;
    const t = e.target as HTMLElement | null;
    // Inside the reveal itself, or on a toolbar toggle button, is not "away".
    // Empty space in the toolbar row IS away, so clicking up top closes too.
    if (t && (t.closest(".wl-reveal") || t.closest(".wl-toolbar button"))) return;
    revealMode = null;
    render();
  };
  document.addEventListener("pointerdown", onDocPointerDown, true);
  // On first load these stores fire repeatedly as data streams in (snapshots,
  // confluence readings, directory), each rebuilding the whole list. If the cursor
  // is over a card during that, the card is destroyed/recreated under it and its
  // hover reveal flickers. So: coalesce store-driven renders to one per frame, AND
  // hold them entirely while the pointer is over the list — flushing once it leaves
  // — so a hovered card stays put during the load. (User actions call render()
  // directly and are never held.)
  let renderRaf = 0;
  let pointerInList = false;
  let pendingRender = false;
  const scheduleRender = () => {
    if (pointerInList) { pendingRender = true; return; }
    if (renderRaf) return;
    renderRaf = requestAnimationFrame(() => { renderRaf = 0; render(); });
  };
  container.addEventListener("pointerenter", () => { pointerInList = true; });
  container.addEventListener("pointerleave", () => {
    pointerInList = false;
    if (pendingRender) { pendingRender = false; render(); }
  });
  const unsubStore = watchlistStore.changed.subscribe(scheduleRender);
  const unsubDirectory = stockDirectory.changed.subscribe(scheduleRender);
  const unsubBoard = confluenceStore.changed.subscribe(scheduleRender);
  const unsubData = confluenceData.changed.subscribe(scheduleRender);
  // Track which chart tab is active so only that symbol's card is tinted.
  const onActiveChart = () => void refreshActiveChart().then(scheduleRender);
  try { chrome.tabs?.onActivated?.addListener(onActiveChart); } catch { /* dev harness */ }
  try { chrome.windows?.onFocusChanged?.addListener(onActiveChart); } catch { /* dev harness */ }
  onStorageChange("openChartTabs", onActiveChart);
  // The card-settings overlay lives on the chart tab; it streams edits back here.
  // Apply them to the working board (found by card id), persist, and re-render.
  onStorageChange<{ cardId?: string; cols?: Column[]; rule?: "all" | "k"; k?: number; os?: number; ob?: number; rel?: RelSettings; catReading?: Card["catReading"]; catLookback?: Card["catLookback"]; filters?: Card["filters"]; breakout?: Card["breakout"]; mode?: Card["mode"] } | null>("chartCardSettingsEdit", (v) => {
    if (!v || !v.cardId) return;
    // Relational tuning (cross/inter) lives outside the card object, in wlRelSettings.
    if (v.rel) { wlRelSettings[v.cardId] = { ...DEFAULT_REL, ...v.rel }; void storageSet("wlRelSettings", wlRelSettings); }
    const card = confluenceStore.working.find((c) => c.id === v.cardId);
    if (card) {
      if (v.cols) card.cols = v.cols;
      if (v.rule) card.rule = v.rule;
      if (typeof v.k === "number") card.k = v.k;
      if (typeof v.os === "number") card.os = v.os;
      if (typeof v.ob === "number") card.ob = v.ob;
      if (v.catReading) card.catReading = { ...card.catReading, ...v.catReading };
      if (v.catLookback) card.catLookback = { ...card.catLookback, ...v.catLookback };
      if (v.filters) card.filters = v.filters;
      if (v.breakout) card.breakout = v.breakout; // optimiser can retune a breakout card's channel (D-115)
      if (v.mode) card.mode = v.mode; // optimiser can retune a follow (Trend continuation) card
      persistWorking();
    }
    render();
  });
  // The card-settings overlay's category switcher (on the chart) asks the scan
  // card to switch lens too, so the two stay in sync as you toggle.
  onStorageChange<{ cardId?: string; category?: Cat } | null>("chartCardCategory", (v) => {
    if (!v || !v.cardId || !v.category) return;
    cardCategory.set(v.cardId, v.category);
    sessionExpanded.add(v.cardId);
    render();
  });
  // The new Setups overlay (D-118) streams library actions back here — apply them to
  // confluenceStore (the popup owns the store) and re-push the refreshed list. Dormant
  // until the gear opens that overlay (a later increment); the plumbing lands first.
  onStorageChange<{ symbol?: string; op?: string; id?: string; on?: boolean; card?: Card } | null>("setupLibraryAction", (v) => {
    if (!v || !v.symbol || !v.op) return;
    const sym = v.symbol;
    void (async () => {
      if (v.op === "live" && v.id) await confluenceStore.setLive(v.id, !!v.on);
      else if (v.op === "duplicate" && v.id) await confluenceStore.duplicateSetup(v.id);
      else if (v.op === "delete" && v.id) await confluenceStore.deleteSetup(v.id);
      else if (v.op === "save" && v.card) await confluenceStore.upsertSetup(v.card);
      else if (v.op === "applyAll" && v.card) await confluenceStore.applySetupToAll(v.card); // D-158 watchlist-wide scan
      // v1: refresh the open builder tab with the updated library (does not steal
      // focus or reopen — just re-pushes the data the tab is already listening for).
      pushBuilderData(sym);
    })();
  });
  // Closing the settings overlay on the chart (its ✕ sets "chartCardSettings" to null)
  // collapses the card whose settings were open, back to its pills — unless it's pinned.
  onStorageChange<unknown>("chartCardSettings", (v) => {
    if (v == null && settingsOpenCardId) {
      const id = settingsOpenCardId;
      settingsOpenCardId = null;
      if (!lockedIds.has(id)) { sessionExpanded.delete(id); render(); }
    }
  });
  // The chart persists strategy edits to the same book keys; keep our cache in
  // sync so the card's list and active selection track the chart live.
  const onBookChange = (changes: Record<string, chrome.storage.StorageChange>, area: string) => {
    if (area !== "local") return;
    let touched = false;
    for (const key of Object.keys(changes)) {
      if (!key.startsWith(BOOK_PREFIX)) continue;
      const sym = key.slice(BOOK_PREFIX.length);
      const nv = changes[key]!.newValue as StudioBook | undefined;
      if (nv && Array.isArray(nv.strategies)) books.set(sym, nv);
      touched = true;
    }
    if (touched) scheduleRender();
  };
  try { chrome.storage.onChanged.addListener(onBookChange); } catch { /* dev harness */ }
  return () => {
    strategyView?.dispose(); strategyView = null;
    unsubStore(); unsubDirectory(); unsubBoard(); unsubData(); offDataMode();
    if (renderRaf) cancelAnimationFrame(renderRaf);
    document.removeEventListener("pointerdown", onDocPointerDown, true);
    try { chrome.tabs?.onActivated?.removeListener(onActiveChart); } catch { /* noop */ }
    try { chrome.windows?.onFocusChanged?.removeListener(onActiveChart); } catch { /* noop */ }
    try { chrome.storage.onChanged.removeListener(onBookChange); } catch { /* noop */ }
    document.getElementById("page-actions")?.replaceChildren();
  };
}
